import { Alert, Button } from 'antd';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useEffect, useRef, useState } from 'react';
import { useVerificationContext } from './VerificationWidget';
import { getEMIAccountMeta } from 'shared/config/store/reducers/accountsSlice';
import { RootState } from 'shared/config/store/store';
import { getUserMeta } from 'shared/config/store/reducers/userSlice';
import { VerificationStatus } from 'entities/user/model/types';
import classes from './VerificationBanner.module.css';

export const VerificationBanner = () => {
  const { formatMessage: t } = useIntl();
  const { openOnfidoModal, onfidoClosedWithoutCompletion, emi, hasOpenedOnfido, onfidoModalOpen, handleRetryVerification } = useVerificationContext();
  const user = useSelector(getUserMeta);
  const emiMeta = useSelector((state: RootState) => getEMIAccountMeta(state, emi));
  const [shouldShow, setShouldShow] = useState(false);
  const onfidoModalOpenRef = useRef(onfidoModalOpen);
  
  useEffect(() => {
    onfidoModalOpenRef.current = onfidoModalOpen;
  }, [onfidoModalOpen]);
  
  const isClientCreated = Object.keys(user.emiStatuses).includes(emi);
  const verificationStatus = emiMeta?.kyc_status;
  
  const shouldShowBannerImmediately = (): boolean => {
    if (!isClientCreated) {
      return false;
    }
    
    if (verificationStatus === VerificationStatus.Rejected || verificationStatus === VerificationStatus.Pending) {
      return true;
    }
    
    if (verificationStatus === VerificationStatus.Init && !onfidoModalOpen) {
      return onfidoClosedWithoutCompletion || hasOpenedOnfido;
    }
    
    return false;
  };
  
  const needsDelayedCheck = (): boolean => {
    return (
      isClientCreated &&
      verificationStatus === VerificationStatus.Init &&
      !onfidoModalOpen &&
      !onfidoClosedWithoutCompletion &&
      !hasOpenedOnfido
    );
  };
  
  useEffect(() => {
    if (shouldShowBannerImmediately()) {
      setShouldShow(true);
      return;
    }
    
    if (needsDelayedCheck()) {
      const timer = setTimeout(() => {
        if (!onfidoModalOpenRef.current) {
          setShouldShow(true);
        }
      }, 500);
      return () => clearTimeout(timer);
    }
    
    setShouldShow(false);
  }, [isClientCreated, verificationStatus, onfidoModalOpen, onfidoClosedWithoutCompletion, hasOpenedOnfido]);

  if (!shouldShow) {
    return null;
  }

  const getBannerContent = () => {
    if (verificationStatus === VerificationStatus.Rejected) {
      return {
        message: t({ id: 'verification.status.failed', defaultMessage: 'Verification failed' }),
        description: t({ id: 'verification.status.failed.desc', defaultMessage: 'Your submitted information or documents was nor approved. You can retry!' }),
        type: 'error' as const,
        buttonText: t({ id: 'verification.retry.buttonLabel', defaultMessage: 'Retry verification' }),
        onButtonClick: handleRetryVerification,
      };
    }
    if (verificationStatus === VerificationStatus.Pending) {
      return {
        message: t({ id: 'verification.status.in_progress', defaultMessage: 'Verification in progress' }),
        description: t({ id: 'verification.status.in_progress.desc', defaultMessage: 'Your client is being prepared now. Please wait and return later!' }),
        type: 'info' as const,
        buttonText: null,
        onButtonClick: null,
      };
    }
    return {
      message: t({ id: 'verification.banner.message', defaultMessage: 'Verification was not completed' }),
      description: t({ id: 'verification.banner.description', defaultMessage: 'Please complete the verification process to continue using your account.' }),
      type: 'warning' as const,
      buttonText: t({ id: 'verification.banner.button', defaultMessage: 'Complete Verification' }),
      onButtonClick: openOnfidoModal,
    };
  };

  const bannerContent = getBannerContent();

  return (
    <Alert
      message={bannerContent.message}
      description={bannerContent.description}
      type={bannerContent.type}
      showIcon
      action={
        bannerContent.buttonText && bannerContent.onButtonClick ? (
          <Button
            size="small"
            type="primary"
            onClick={bannerContent.onButtonClick}
            style={{ marginTop: 8 }}
          >
            {bannerContent.buttonText}
          </Button>
        ) : null
      }
      className={classes.banner}
      closable={false}
    />
  );
};

